import { directionLabel } from './catalog';

const EARTH_RADIUS_M = 6371000;
const toRadians = (degrees) => (degrees * Math.PI) / 180;
const toDegrees = (radians) => (radians * 180) / Math.PI;

export const DEFAULT_PROGRESSION_KPH = 40;

export function distanceMeters(from, to) {
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const haversine = Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

export function bearingBetween(from, to) {
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const y = Math.sin(deltaLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
  return (toDegrees(Math.atan2(y, x)) + 360) % 360;
}

/** Splits a phase that runs past the end of the cycle into drawable segments. */
export function wrapSegments(start, length, cycle) {
  if (length <= 0 || cycle <= 0) return [];
  const from = ((start % cycle) + cycle) % cycle;
  const span = Math.min(length, cycle);
  if (from + span <= cycle) return [{ start: from, length: span }];
  return [
    { start: from, length: cycle - from },
    { start: 0, length: span - (cycle - from) },
  ];
}

function orderAlongCorridor(stops) {
  const lats = stops.map((stop) => stop.lat);
  const lngs = stops.map((stop) => stop.lng);
  const latSpan = Math.max(...lats) - Math.min(...lats);
  const lngSpan = Math.max(...lngs) - Math.min(...lngs);
  const key = lngSpan >= latSpan ? 'lng' : 'lat';
  return [...stops].sort((a, b) => a[key] - b[key]);
}

/** Splits arm flows into the two travel senses of the corridor. */
function flowsBySense(stops, corridorBearing) {
  let forward = 0;
  let backward = 0;
  stops.forEach((stop) => {
    (stop.arms || []).forEach((arm) => {
      const alignment = Math.cos(toRadians(Number(arm.bearing || 0) - corridorBearing));
      if (alignment >= 0) forward += Number(arm.flow || 0);
      else backward += Number(arm.flow || 0);
    });
  });
  return { forward, backward };
}

function corridorFlow(stop, corridorBearing) {
  return (stop.arms || []).reduce((total, arm) => {
    const alignment = Math.cos(toRadians(Number(arm.bearing || 0) - corridorBearing));
    return alignment >= 0 ? total + Number(arm.flow || 0) : total;
  }, 0);
}

/**
 * Builds the coordinated corridor described in the SEMAFIX specification:
 * a common cycle, one offset per signal relative to the reference intersection,
 * the green band, the progression speed and the priority direction.
 */
export function computeGreenWave(stops, { speedKph = DEFAULT_PROGRESSION_KPH } = {}) {
  if (!Array.isArray(stops) || stops.length < 2) return null;

  let ordered = orderAlongCorridor(stops);
  let directionBearing = bearingBetween(ordered[0], ordered[ordered.length - 1]);

  // The green wave follows the prevailing demand along the corridor.
  const { forward, backward } = flowsBySense(ordered, directionBearing);
  if (backward > forward) {
    ordered = [...ordered].reverse();
    directionBearing = (directionBearing + 180) % 360;
  }

  const commonCycle = ordered.reduce(
    (cycle, stop) => Math.max(cycle, Number(stop.green || 0) + Number(stop.yellow || 0) + Number(stop.red || 0)),
    0,
  );
  let cumulativeTravel = 0;
  const rows = ordered.map((stop, index) => {
    const previous = ordered[index - 1];
    const override = Number(stop.segmentDistance);
    const distance = index === 0
      ? 0
      : (Number.isFinite(override) && override > 0 ? override : distanceMeters(previous, stop));
    const segmentSpeed = Number(stop.segmentSpeedKph) > 0 ? Number(stop.segmentSpeedKph) : Number(speedKph);
    const travelTime = distance / Math.max(1, segmentSpeed) * 3.6;
    cumulativeTravel += travelTime;
    const green = Number(stop.green || 0);
    const yellow = Number(stop.yellow || 0);
    return {
      id: stop.id,
      signalId: stop.signalId,
      name: stop.name,
      maneuverId: stop.maneuverId,
      maneuverTitle: stop.maneuverTitle,
      timingSource: stop.timingSource,
      lat: stop.lat,
      lng: stop.lng,
      green,
      yellow,
      red: Math.max(0, commonCycle - green - yellow),
      distance: Math.round(distance),
      travelTime: Math.round(travelTime),
      offset: commonCycle > 0
        ? (Math.round(cumulativeTravel) + Number(stop.startGreen || 0)) % commonCycle
        : 0,
      flow: Math.round(corridorFlow(stop, directionBearing)),
      isReference: index === 0,
    };
  });

  return {
    speedKph: Number(speedKph),
    commonCycle,
    greenBand: rows.reduce((min, row) => Math.min(min, row.green), Number.POSITIVE_INFINITY),
    directionBearing: Math.round(directionBearing),
    directionLabel: directionLabel(directionBearing),
    priorityFlow: Math.max(forward, backward),
    totalDistance: rows.reduce((total, row) => total + row.distance, 0),
    totalTravelTime: Math.round(cumulativeTravel),
    rows,
  };
}
